import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { Box, Button } from "@mui/material";
import ProductsTable from "../../components/Tables/ProductsTable";
import { exportToCSV, fetchWrapper, fetchWrapperNoAuth, SERVER } from "../../utils/helpers";
import ShippmentsModal from "../../components/Modals/ShippmentsModal";
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import DeleteIcon from '@mui/icons-material/Delete';
import CustomCircularProgress from "../../components/Loaders/CustomCircularProgress";
import CatalogFilterBar from "../../components/Tables/CatalogFilterBar";
import ConfirmModal from "../../components/Modals/ConfirmModal";

function Catalog(props) {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { encodedAuth, user, password } = useAuth();
  const location = useLocation();
  const { batchName } = location.state || [];

  const [open, setOpen] = useState(false);

  const [inProgress, setInProgress] = useState(false);
  const [isDownloaded, setIsDownloaded] = useState(false);

  const [products, setProducts] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [batch, setbatch] = useState(batchName || "none");
  const [nextCursor, setNextCursor] = useState("*");
  const [batches, setbatches] = useState([]);
  const [searchFilterType, setSearchFilterType] = useState("SKU");
  const [searchQuery, setSearchQuery] = useState("");
  const [publishedStatus, setPublishedStatus] = useState('PUBLISHED');
  const [markedProducts, setMarkedProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [shippmentOpenModal, setShippmentOpenModal] = useState(false);
  const priorityOrder = { "PUBLISHED": 1, "UNPUBLISHED": 2 };

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const getAllItems = async (tempNextCursor) => {
    setSearchQuery("");
    if (products.length === 0) {
      setIsLoading(true);
    }
    let tempProducts = [];

    const body = {
      batchName: "none",
      publishedStatus: publishedStatus
    }

    try {
      const responseData = await fetchWrapper(`${SERVER}/get_all_items/${user}/${tempNextCursor}`, user, password, encodedAuth, { method: 'POST', body: body });
      let tempServerProducts = responseData.items.map((item) => { return { ...item, price: item.price.amount } });
      tempProducts = products.concat(tempServerProducts);
      const uniqueArray = Object.values(
        tempProducts.reduce((acc, item) => {
          if (!acc[item.sku] || new Date(item.last_modified) > new Date(acc[item.sku].last_modified)) {
            acc[item.sku] = item;
          }
          return acc;
        }, {})
      );
      setProducts(uniqueArray);
      // setMarkedProducts(uniqueArray.filter((product) => product.publishedStatus === "PUBLISHED"));
      setTotalItems(responseData.totalItems)

      if (!responseData.nextCursor) {
        setNextCursor(null);
      } else {
        setNextCursor(responseData.nextCursor);
      }
    } catch (error) {
      console.error(`All Items request failed:`, error.message);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  }

  const handleSearchByBatch = async (batchFilterName) => {
    setPage(0);
    if (batchFilterName === "") {
      resetFilters();
      return;
    }

    setIsLoading(true);
    setProducts([]);

    const body = { batch: batchFilterName };

    // Step 1: Get all SKUs from DB
    let skuArray = [];
    try {
      const response = await fetchWrapperNoAuth(`${SERVER}/falcon_get_skus_by_batch/${user}`, user, password, { method: 'POST', body });
      skuArray = response;
    } catch (error) {
      console.error(`Error fetching SKUs:`, error.message);
      setIsLoading(false);
      return;
    }

    // Step 2: Fetch item catalogs for each SKU
    try {
      const allProducts = (
        await Promise.all(
          skuArray.map(async (tempSku) => {
            try {
              const body = {
                email: user,
                productId: tempSku,
                productType: "SKU"
              };
              const responseData = await fetchWrapper(`${SERVER}/get_an_item_catalog`, user, password, encodedAuth, { method: 'POST', body });
              return responseData.map((item) => ({ ...item, price: item.price.amount }));
            } catch (error) {
              console.error(`An item request failed:`, error.message);
              return [];
            }
          })
        )
      ).flat();

      setProducts(allProducts);
      // setMarkedProducts(allProducts.filter((product) => product.publishedStatus === "PUBLISHED"));
    } catch (error) {
      console.error(`Error fetching item catalogs:`, error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchByQuery = async (query) => {
    if (!query) {
      alert("Please type somthing to search")
    }

    setPage(0);
    setProducts([]);
    setbatch("");
    setIsLoading(true);

    if (searchFilterType === "SKU") {
      try {
        const body = {
          email: user,
          productId: query,
          productType: "SKU"
        };
        const responseData = await fetchWrapper(`${SERVER}/get_an_item_catalog`, user, password, encodedAuth, { method: 'POST', body });
        let tempServerProducts = responseData.map((item) => { return { ...item, price: item.price.amount } });

        setProducts(tempServerProducts);
        // setMarkedProducts(tempServerProducts.filter((product) => product.publishedStatus === "PUBLISHED"));
      } catch (error) {
        console.error(`An Items request failed:`, error.message);
        setIsLoading(false);
        return;
      }
    } else if (searchFilterType === "Item ID") {
      try {
        const body = {
          email: user,
          productId: query,
          productType: "ITEM_ID"
        };
        const responseData = await fetchWrapper(`${SERVER}/get_an_item_catalog`, user, password, encodedAuth, { method: 'POST', body });
        let tempServerProducts = responseData.map((item) => { return { ...item, price: item.price.amount } });

        setProducts(tempServerProducts);
        // setMarkedProducts(tempServerProducts.filter((product) => product.publishedStatus === "PUBLISHED"));
      } catch (error) {
        console.error(`An Items request failed:`, error.message);
        setIsLoading(false);
        return;
      }
    } else {
      alert("Coming Soon!")
    }

    setIsLoading(false);
  };

  const handleSearchByPublishedStatus = async (status) => {
    setPage(0);
    setSearchQuery("");
    setProducts([]);
    setbatch("");
    setIsLoading(true);

    const body = {
      batchName: "none",
      publishedStatus: status
    }

    try {
      const responseData = await fetchWrapper(`${SERVER}/get_all_items/${user}/*`, user, password, encodedAuth, { method: 'POST', body: body });
      let tempServerProducts = responseData.items.map((item) => { return { ...item, price: item.price.amount } });
      const uniqueArray = Object.values(
        tempServerProducts.reduce((acc, item) => {
          if (!acc[item.sku] || new Date(item.last_modified) > new Date(acc[item.sku].last_modified)) {
            acc[item.sku] = item;
          }
          return acc;
        }, {})
      );
      setProducts(uniqueArray);
      // setMarkedProducts(uniqueArray.filter((product) => product.publishedStatus === "PUBLISHED"));
      setTotalItems(responseData.totalItems)

      if (!responseData.nextCursor) {
        setNextCursor(null);
      } else {
        setNextCursor(responseData.nextCursor);
      }
    } catch (error) {
      console.error(`All Items request failed:`, error.message);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  }

  const resetFilters = () => {
    setProducts([]);
    setbatch("");
    setSearchQuery("");
    setIsLoading(true);
    getAllItems("*");
  };

  // Pagination
  const handleChangePage = (event, newPage) => {
    if ((newPage * 2) * rowsPerPage >= products.length && (batch === "none" || batch === "")) {
      getAllItems(nextCursor);
    }
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const sortedProducts = [...products].sort((a, b) => {
    const rankA = priorityOrder[a.publishedStatus] || 3;
    const rankB = priorityOrder[b.publishedStatus] || 3;

    if (rankA !== rankB) {
      return rankA - rankB; // Sort by priority first
    }

    // If priority is the same, sort by last_modified (assuming it's a date string or timestamp)
    return new Date(b.last_modified) - new Date(a.last_modified);
  });

  const paginatedProducts = sortedProducts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleExportToCsv = () => {
    setInProgress(true);
    const headers = ['Item Name', 'SKU', 'Price', 'Quantity', 'Status', 'Lifecycle Status'];
    const objects = products.map((product) => {
      return {
        'Item Name': product.productName,
        'SKU': product.sku,
        'Price': product.price || 0,
        'Quantity': product.qty || 0,
        'Status': product.publishedStatus,
        'Lifecycle Status': product.lifecycleStatus
      }
    });

    exportToCSV(headers, objects);
    setIsDownloaded(true);
    setInProgress(false);
  };

  const deleteBatch = async () => {
    console.log(batch)
  }

  useEffect(() => {
    const getBatches = async () => {
      const url = `${SERVER}/falcon_get_batch_names/${user}`;

      try {
        const response = await fetchWrapperNoAuth(url, user, password);
        setbatches(response.filter(item => item !== "" && item !== "none" && item !== undefined && item !== null).sort());
      } catch (error) {
        console.error(`All Batches request failed:`, error.message);
        return;
      }
    }
    getBatches();

    if (products.length === 0) {
      getAllItems(nextCursor);
    }
  }, []);

  const btnStyle = {
    my: 3,
    mx: 1,
    borderRadius: 20,
    display: 'block',
    fontSize: 18,
    // fontWeight: "bold",
    px: 4,
    color: theme.button.color,
    backgroundColor: theme.button.background,
    fontSize: 14,
    fontFamily: theme.fontFamily,
  };

  return (
    <>
      <Helmet>
        <title>Inventory Falcon</title>
        <meta
          name="description"
          content="The best inventory management software for Walmart sellers is coming soon! To join our beta program, please email us at: team@inventoryfalcon.com"
        />
      </Helmet>
      <Box sx={{ bgcolor: theme.background.primary, height: `-webkit-fill-available`, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center", mx: '10px', }}>
          <h1
            style={{
              fontSize: props.isMobile ? "3vh" : "2vw",
              fontWeight: "380",
              textAlign: "center",
              color: theme.text.primary,
            }}
          >
            Catalog
          </h1>
          <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center" }}>
            {/* {batch !== "none" && batch !== "" && batch !== "All Batches" && (
              <Button
                variant="contained"
                onClick={() => setOpen(true)}
                sx={{ ...btnStyle, px: 2, display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", }}
                endIcon={<DeleteIcon />}
              >
                Delete "{batch}" batch
              </Button>
            )} */}

            <Button
              variant="contained"
              onClick={handleExportToCsv}
              disabled={isDownloaded}
              sx={{ ...btnStyle, px: 2, display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", }}
              startIcon={<FileDownloadRoundedIcon />}
            >
              {inProgress ? (
                <CustomCircularProgress />
              ) : (
                <>
                  Export to CSV
                </>
              )}

            </Button>

            <Button sx={btnStyle} onClick={() => setShippmentOpenModal(true)} variant="contained" >
              Ship All Selected Items {markedProducts.length > 0 && `(${markedProducts.length})`}
            </Button>
          </Box>
        </Box>

        <CatalogFilterBar
          totalItems={totalItems}
          searchFilterType={searchFilterType}
          setSearchFilterType={setSearchFilterType}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          handleSearchByQuery={handleSearchByQuery}
          publishedStatus={publishedStatus}
          setPublishedStatus={setPublishedStatus}
          handleSearchByPublishedStatus={handleSearchByPublishedStatus}
          batches={batches}
          batchFilter={batch}
          setBatchFilter={setbatch}
          handleSearchByBatch={handleSearchByBatch}
          resetFilters={resetFilters}
        />

        <ProductsTable
          products={products}
          isLoading={isLoading}
          markedProducts={markedProducts}
          setMarkedProducts={setMarkedProducts}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          page={page}
          rowsPerPage={rowsPerPage}
          paginatedProducts={paginatedProducts}
          totalItems={totalItems}
        />

        <ShippmentsModal open={shippmentOpenModal} setOpen={setShippmentOpenModal} items={products.filter(item => markedProducts.includes(item.sku))} />

        {/* <ConfirmModal open={open} setOpen={setOpen} message={`Are you sure you want to delete ${batch} batch ?`} confirm={deleteBatch} /> */}
      </Box>
    </>
  );
}
export default Catalog;
